import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Plus,
  DotsThree,
  Trash,
  SpinnerGap,
  CheckCircle,
  Clock,
  UploadSimple,
  DownloadSimple,
  FileText,
  XCircle,
  FileArrowUp,
  PencilSimple,
  MagnifyingGlass,
  Eye,
  X,
  Key,
  UsersThree,
  SignOut,
  ArrowCounterClockwise,
} from '@phosphor-icons/react';
import { FilterPanel, FilterField } from '../../components/FilterPanel';
import { ExportPreviewModal } from '../../components/ExportPreviewModal';
import { fmtINR } from '../../utils/currency';
import { toTitleCase } from '../../utils/textFormat';
import MergeUsersDialog from '../../components/MergeUsersDialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_ROLES = [
  { value: 'sales_recruiter', label: 'Sales Recruiter' },
  { value: 'sales_lead', label: 'Sales Lead' },
  { value: 'sales_manager', label: 'Sales Manager' },
  { value: 'sales_assistant_manager', label: 'Sales Asst. Manager' },
  { value: 'sales_technical_recruiter', label: 'Sales Recruiter & Technical Recruiter' },
  { value: 'technical_recruiter', label: 'Technical Recruiter' },
  { value: 'recruiting_lead', label: 'Recruiting Lead' },
  { value: 'recruiting_manager', label: 'Recruiting Manager' },
  { value: 'recruiting_assistant_manager', label: 'Recruiting Asst. Manager' },
  { value: 'onsite_manager', label: 'Onsite Manager' },
  { value: 'people_admin', label: 'People Admin' },
  { value: 'superadmin', label: 'Super Admin' },
  { value: 'ceo', label: 'CEO' },
];

const formatCurrency = (amount) => fmtINR(amount);
const labelForRole = (role, roles = FALLBACK_ROLES) =>
  roles.find((r) => r.value === role)?.label || role?.replace(/_/g, ' ');

const MANAGER_DROPDOWN_ROLES = new Set([
  'sales_manager', 'recruiting_manager', 'sales_lead', 'recruiting_lead',
  'sales_assistant_manager', 'recruiting_assistant_manager', 'onsite_manager', 'ceo',
]);

const UserManagementPage = () => {
  const { user: currentUser, isAdmin: isFullAdmin, isPeopleAdmin, canManageUsers } = useAuth();
  const navigate = useNavigate();
  const canSeeSalary = isFullAdmin;
  const [roles, setRoles] = useState(FALLBACK_ROLES);
  const assignableRoles = useMemo(() => {
    const blocked = isPeopleAdmin ? ['superadmin', 'ceo', 'people_admin'] : [];
    return roles.filter((r) => !blocked.includes(r.value));
  }, [roles, isPeopleAdmin]);
  const getRoleLabel = (role) => labelForRole(role, roles);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [showExport, setShowExport] = useState(false);
  const [orgFilter, setOrgFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [salaryFilter, setSalaryFilter] = useState('all');

  // Create user
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({ email: '', name: '', role: '', team: '', organization: '', work_location: '', reporting_managers: [], onsite_managers: [] });

  // Organizations list
  const [organizations, setOrganizations] = useState([]);

  // Edit user
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editData, setEditData] = useState({});

  // Password reset
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Placement details modal
  const [showPlacementModal, setShowPlacementModal] = useState(false);
  const [placementDetails, setPlacementDetails] = useState([]);
  const [placementLoading, setPlacementLoading] = useState(false);
  const [detailUser, setDetailUser] = useState(null);
  const [detailType, setDetailType] = useState('placements'); // 'placements' or 'backouts' or 'supported'

  // Bulk upload
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadOptions, setUploadOptions] = useState({ sendEmails: true, selectedFile: null });
  const fileInputRef = useRef(null);

  // Group by team toggle
  const [groupByTeam, setGroupByTeam] = useState(false);

  // Inline editing
  const [editingCell, setEditingCell] = useState(null); // { userId, field }
  const [editingValue, setEditingValue] = useState('');
  const [savingInline, setSavingInline] = useState(false);

  // Work locations
  const [workLocations, setWorkLocations] = useState([]);
  const [newLocation, setNewLocation] = useState('');
  const [showLocationsModal, setShowLocationsModal] = useState(false);

  useEffect(() => { fetchUsers(); fetchWorkLocations(); fetchOrganizations(); fetchUnassigned(); fetchRoles(); }, []);

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/rbac/roles`, { withCredentials: true });
      const list = (res.data || [])
        .map((r) => ({ value: r.value, label: r.label || r.value }))
        .filter((r) => r.value);
      if (!list.length) return;
      const seen = new Set(list.map((r) => r.value));
      FALLBACK_ROLES.forEach((r) => { if (!seen.has(r.value)) list.push(r); });
      setRoles(list);
    } catch (e) {
      console.error('Failed to fetch roles:', e);
    }
  };

  const fetchUnassigned = async () => {
    if (!canManageUsers) return;
    try {
      const res = await axios.get(`${API_URL}/api/admin/users/unassigned`, { withCredentials: true });
      setUnassignedUsers(res.data?.users || []);
    } catch (e) { console.error('Failed to fetch unassigned users:', e); }
  };

  const activeUserOptions = useMemo(() =>
    users
      .filter((u) => u.is_active !== false && u.id !== editUser?.id)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
  [users, editUser]);

  const reportingManagerOptions = useMemo(() => {
    const preferred = activeUserOptions.filter((u) => MANAGER_DROPDOWN_ROLES.has(u.role));
    const seen = new Set(preferred.map((u) => u.id));
    const extras = [];
    for (const id of [editData.manager_id, ...(editData.reporting_managers || [])].filter(Boolean)) {
      if (seen.has(id)) continue;
      const match = activeUserOptions.find((u) => u.id === id);
      if (match) {
        extras.push(match);
        seen.add(id);
      }
    }
    return [...preferred, ...extras].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [activeUserOptions, editData.manager_id, editData.reporting_managers]);

  const fetchOrganizations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/organizations`, { withCredentials: true });
      setOrganizations(res.data);
    } catch (e) { console.error('Failed to fetch orgs:', e); }
  };

  const [showInactive, setShowInactive] = useState(false);
  const [unassignedUsers, setUnassignedUsers] = useState([]);
  const [showMergeModal, setShowMergeModal] = useState(false);
  // Row-triggered merge: primary user (target) when clicking the per-row Merge button
  const [mergePrimaryUser, setMergePrimaryUser] = useState(null);

  const fetchUsers = async (inactive = false) => {
    try {
      // Active list uses /api/admin/user-directory; Inactive uses /api/users?only_inactive=true.
      if (inactive) {
        const response = await axios.get(`${API_URL}/api/users?only_inactive=true`, { withCredentials: true });
        setUsers(response.data || []);
      } else {
        const response = await axios.get(`${API_URL}/api/admin/user-directory`, { withCredentials: true });
        // user-directory already filters out inactive (handled server-side); if not, do client-side fallback.
        setUsers((response.data || []).filter(u => u.is_active !== false));
      }
    } catch (err) {
      // Fallback to basic users endpoint
      try {
        const response = await axios.get(`${API_URL}/api/users${inactive ? '?only_inactive=true' : ''}`, { withCredentials: true });
        setUsers(response.data || []);
      } catch (e) { console.error('Failed to fetch users:', e); }
    } finally { setLoading(false); }
  };

  const fetchWorkLocations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/work-locations`, { withCredentials: true });
      setWorkLocations(res.data || []);
    } catch (e) { console.error('Failed to fetch work locations:', e); }
  };

  const addWorkLocation = async () => {
    const name = newLocation.trim();
    if (!name) return;
    try {
      await axios.post(`${API_URL}/api/work-locations`, { name }, { withCredentials: true });
      setNewLocation('');
      fetchWorkLocations();
    } catch (err) { alert(err.response?.data?.detail || err.message); }
  };

  const deleteWorkLocation = async (locationName) => {
    if (!window.confirm(`Remove "${locationName}" from locations?`)) return;
    try {
      await axios.delete(`${API_URL}/api/work-locations/${encodeURIComponent(locationName)}`, { withCredentials: true });
      fetchWorkLocations();
    } catch (err) { alert(err.response?.data?.detail || err.message); }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setCreateLoading(true);
    try {
      await axios.post(`${API_URL}/api/users`, formData, { withCredentials: true });
      setSuccess('User created successfully! Default password: Demo@123');
      setFormData({ email: '', name: '', role: '', team: '', organization: '', work_location: '', reporting_managers: [], onsite_managers: [] });
      fetchUsers();
      setTimeout(() => { setShowCreateModal(false); setSuccess(''); }, 2000);
    } catch (err) { setError(getApiErrorMessage(err)); }
    finally { setCreateLoading(false); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API_URL}/api/users/${userId}`, { withCredentials: true });
      fetchUsers();
    } catch (err) { alert('Failed to delete: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleMoveToLeft = async (user) => {
    if (!window.confirm(`Move "${user.name}" to Left Employees?\n\nThis will:\n• Remove them from all teams (member list, manager/lead slots)\n• Clear them as a Manager/Lead from other users' records\n• Move them to the "Inactive / Left Employees" tab\n\nThey can be restored later from the Inactive tab.`)) return;
    try {
      const res = await axios.post(`${API_URL}/api/users/${user.id}/move-to-left`, {}, { withCredentials: true });
      const s = res.data?.summary || {};
      toast.success(`Moved to Left ✓  ${s.teams_member_removed || 0} team memberships · ${s.teams_role_cleared || 0} role slots · ${s.users_manager_cleared + s.users_lead_cleared || 0} refs cleared`);
      fetchUsers(showInactive);
      fetchUnassigned();
    } catch (err) { toast.error('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleRestoreUser = async (user) => {
    if (!window.confirm(`Restore "${user.name}" as an active employee?\n\nThey will move back to the Active tab. Use the Edit dialog to re-assign their team / manager / lead if needed.`)) return;
    try {
      await axios.post(`${API_URL}/api/users/${user.id}/restore`, {}, { withCredentials: true });
      toast.success(`${user.name} restored ✓`);
      fetchUsers(showInactive);
      fetchUnassigned();
    } catch (err) { toast.error('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const openEditModal = async (user) => {
    setEditUser(user);
    setError('');
    setShowEditModal(true);
    try {
      const res = await axios.get(`${API_URL}/api/users/${user.id}`, { withCredentials: true });
      const full = res.data || user;
      setEditData({
        employee_id: full.employee_id || '',
        first_name: full.first_name || '',
        last_name: full.last_name || '',
        designation: full.designation || '',
        direct_number: full.direct_number || '',
        team: full.team || '',
        salary: full.salary || '',
        role: full.role || '',
        reporting_managers: full.reporting_managers || [],
        onsite_managers: full.onsite_managers || [],
        work_location: full.work_location || '',
        organization: full.organization || '',
        manager_id: full.manager_id || '',
        reporting_lead_id: full.reporting_lead_id || '',
        is_active: full.is_active !== false,
      });
    } catch {
      setEditData({
        employee_id: user.employee_id || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        designation: user.designation || '',
        direct_number: user.direct_number || '',
        team: user.team || '',
        salary: user.salary || '',
        role: user.role || '',
        reporting_managers: user.reporting_managers || [],
        onsite_managers: user.onsite_managers || [],
        work_location: user.work_location || '',
        organization: user.organization || '',
        manager_id: user.manager_id || '',
        reporting_lead_id: user.reporting_lead_id || '',
        is_active: user.is_active !== false,
      });
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setError(''); setEditLoading(true);
    try {
      const payload = { ...editData };
      if (!canSeeSalary) delete payload.salary;
      else if (payload.salary) payload.salary = parseFloat(payload.salary) || 0;
      await axios.put(`${API_URL}/api/users/${editUser.id}`, payload, { withCredentials: true });
      setShowEditModal(false);
      fetchUsers();
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        setError('Session expired or not authenticated. Please log out and log in again, then retry.');
      } else {
        setError(getApiErrorMessage(err));
      }
    }
    finally { setEditLoading(false); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) return alert('Password must be at least 6 characters');
    try {
      await axios.post(`${API_URL}/api/admin/users/${passwordUser.id}/reset-password`, { password: newPassword }, { withCredentials: true });
      alert(`Password reset for ${passwordUser.name || passwordUser.email}`);
      setShowPasswordModal(false);
      setNewPassword('');
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const openPlacementDetails = async (user, type) => {
    setDetailUser(user);
    setDetailType(type);
    setPlacementLoading(true);
    setShowPlacementModal(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/user/${user.id}/placement-details`, { withCredentials: true });
      if (type === 'backouts') {
        setPlacementDetails(res.data.filter(p => p.status === 'backed_out'));
      } else {
        setPlacementDetails(res.data.filter(p => p.status !== 'backed_out'));
      }
    } catch (err) { console.error(err); setPlacementDetails([]); }
    finally { setPlacementLoading(false); }
  };

  const openSupportedDetails = async (user) => {
    setDetailUser(user);
    setDetailType('supported');
    setPlacementLoading(true);
    setShowPlacementModal(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/user/${user.id}/supported-placements`, { withCredentials: true });
      setPlacementDetails(res.data);
    } catch (err) { console.error(err); setPlacementDetails([]); }
    finally { setPlacementLoading(false); }
  };

  // Bulk upload handlers
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/csv-template`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', 'user_upload_template.xlsx');
      document.body.appendChild(link); link.click(); link.remove();
    } catch (err) { alert('Failed to download template'); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && (file.name.endsWith('.csv') || file.name.endsWith('.xlsx'))) setUploadOptions({ ...uploadOptions, selectedFile: file });
    else if (file) alert('Please select a CSV or XLSX file');
  };

  const handleUpload = async () => {
    if (!uploadOptions.selectedFile) return;
    setUploadLoading(true); setUploadResult(null);
    try {
      const fd = new FormData(); fd.append('file', uploadOptions.selectedFile);
      fd.append('send_emails', uploadOptions.sendEmails.toString());
      const response = await axios.post(`${API_URL}/api/users/bulk-upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResult(response.data); fetchUsers();
    } catch (err) { setUploadResult({ error: err.response?.data?.detail || err.message }); }
    finally { setUploadLoading(false); }
  };

  const resetUpload = () => { setUploadOptions({ sendEmails: true, selectedFile: null }); setUploadResult(null); if (fileInputRef.current) fileInputRef.current.value = ''; };

  const startInlineEdit = (userId, field, currentValue) => {
    setEditingCell({ userId, field });
    setEditingValue(currentValue || '');
  };

  const cancelInlineEdit = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const saveInlineEdit = async () => {
    if (!editingCell) return;
    setSavingInline(true);
    try {
      const payload = { [editingCell.field]: editingCell.field === 'salary' ? (parseFloat(editingValue) || 0) : editingValue };
      await axios.put(`${API_URL}/api/users/${editingCell.userId}`, payload, { withCredentials: true });
      fetchUsers();
    } catch (err) { console.error('Inline save failed:', err); }
    finally { setSavingInline(false); setEditingCell(null); setEditingValue(''); }
  };

  const handleInlineKeyDown = (e) => {
    if (e.key === 'Enter') saveInlineEdit();
    else if (e.key === 'Escape') cancelInlineEdit();
  };

  const InlineEditCell = ({ userId, field, value, isCurrency, className = '' }) => {
    const isEditing = editingCell?.userId === userId && editingCell?.field === field;
    if (isEditing) {
      return (
        <Input
          autoFocus
          type={field === 'salary' ? 'number' : 'text'}
          value={editingValue}
          onChange={(e) => setEditingValue(e.target.value)}
          onBlur={saveInlineEdit}
          onKeyDown={handleInlineKeyDown}
          className="h-7 text-xs px-1.5 w-full min-w-[60px]"
          data-testid={`inline-edit-${field}-${userId}`}
        />
      );
    }
    const displayVal = isCurrency && value ? formatCurrency(value) : (value || '-');
    return (
      <span
        onClick={() => currentUser?.email !== users.find(u => u.id === userId)?.email && startInlineEdit(userId, field, value)}
        className={`cursor-pointer hover:bg-blue-50 hover:text-blue-700 px-1 py-0.5 rounded transition-colors ${className}`}
        title="Click to edit"
        data-testid={`inline-cell-${field}-${userId}`}
      >
        {displayVal}
      </span>
    );
  };

  // Filtering
  const teams = [...new Set(users.map(u => u.team).filter(Boolean))].sort();
  const unassignedIds = useMemo(() => new Set(unassignedUsers.map((u) => u.id)), [unassignedUsers]);
  const filtered = users.filter(u => {
    const matchSearch = !searchTerm || (() => {
      const s = searchTerm.toLowerCase();
      return (u.name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s) || u.employee_id?.toLowerCase().includes(s) ||
              u.first_name?.toLowerCase().includes(s) || u.last_name?.toLowerCase().includes(s) ||
              u.role?.replace(/_/g, ' ').toLowerCase().includes(s) ||
              u.team?.toLowerCase().includes(s) || u.organization?.toLowerCase().includes(s) ||
              u.work_location?.toLowerCase().includes(s) || u.designation?.toLowerCase().includes(s) ||
              u.direct_number?.toLowerCase().includes(s));
    })();
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    const matchTeam = teamFilter === 'all'
      || (teamFilter === '__unassigned__' ? unassignedIds.has(u.id) : u.team === teamFilter);
    const matchOrg = orgFilter === 'all' || u.organization === orgFilter;
    const matchDept = deptFilter === 'all' || (deptFilter === 'sales' ? ['sales_recruiter', 'sales_technical_recruiter', 'sales_lead', 'sales_manager', 'sales_assistant_manager'].includes(u.role) : deptFilter === 'recruiting' ? ['technical_recruiter', 'sales_technical_recruiter', 'recruiting_lead', 'recruiting_manager', 'recruiting_assistant_manager'].includes(u.role) : deptFilter === 'admin' ? ['superadmin', 'ceo'].includes(u.role) : true);
    const matchStatus = statusFilter === 'all' || (statusFilter === 'verified' ? u.is_verified : !u.is_verified);
    const matchSalary = salaryFilter === 'all' || (salaryFilter === 'set' ? (u.salary > 0) : !(u.salary > 0));
    return matchSearch && matchRole && matchTeam && matchOrg && matchDept && matchStatus && matchSalary;
  });
  const activeFilters = [roleFilter, teamFilter, orgFilter, deptFilter, statusFilter, salaryFilter].filter(f => f !== 'all').length;
  const allOrgs = [...new Set(users.map(u => u.organization).filter(Boolean))];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit'] tracking-tight">Employee Directory</h1>
          <p className="text-slate-500 mt-1">Manage employees, roles, and team assignments. Click any cell to edit inline.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isFullAdmin && <Button variant="outline" size="sm" onClick={() => setShowLocationsModal(true)} className="gap-1.5 text-xs" data-testid="manage-locations-btn">
            Manage Locations
          </Button>}
          <Button variant="outline" onClick={() => setShowExport(true)} className="gap-1.5" data-testid="export-users-btn">
            <DownloadSimple size={16} /> Export to Excel
          </Button>
          {isFullAdmin && <Button variant="outline" onClick={() => setShowUploadModal(true)} data-testid="bulk-upload-btn">
            <UploadSimple size={18} className="mr-2" /> Bulk Upload
          </Button>}
          {isFullAdmin && <Button variant="outline" onClick={() => setShowMergeModal(true)} className="gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="merge-users-btn">
            <UsersThree size={16} /> Merge Duplicates
          </Button>}
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => { setError(''); setSuccess(''); setShowCreateModal(true); }} data-testid="create-user-btn">
            <Plus size={18} className="mr-2" /> Create User
          </Button>
        </div>
      </div>

      {/* Active / Inactive Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {[
          { key: 'active', label: 'Active Employees' },
          { key: 'inactive', label: 'Inactive / Left Employees' },
        ].map(tab => {
          const isActive = (tab.key === 'active') !== showInactive;
          return (
            <button
              key={tab.key}
              data-testid={`dir-tab-${tab.key}`}
              onClick={() => { const inact = tab.key === 'inactive'; setShowInactive(inact); fetchUsers(inact); }}
              className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 ${isActive ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Total Users', val: users.length, color: 'bg-slate-100 text-slate-700' },
          { label: 'Verified', val: users.filter(u => u.is_verified).length, color: 'bg-emerald-50 text-emerald-700' },
          { label: 'Pending', val: users.filter(u => !u.is_verified).length, color: 'bg-amber-50 text-amber-700' },
          { label: 'Organizations', val: new Set(users.map(u => u.organization).filter(Boolean)).size, color: 'bg-purple-50 text-purple-700' },
          { label: 'Unassigned', val: unassignedUsers.length, color: 'bg-amber-50 text-amber-700', onClick: () => setTeamFilter('__unassigned__') },
          ...(canSeeSalary ? [{ label: 'Salary Set', val: users.filter(u => u.salary > 0).length, color: 'bg-blue-50 text-blue-700' }] : []),
        ].map((stat) => (
          <Card
            key={stat.label}
            className={`border-slate-200 ${stat.onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
            onClick={stat.onClick}
            data-testid={stat.label === 'Unassigned' ? 'stat-unassigned' : undefined}
          >
            <CardContent className="p-4">
              <p className={`text-2xl font-bold ${stat.color.split(' ')[1]}`}>{stat.val}</p>
              <p className="text-sm text-slate-500">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Search by name, email, role, team, organization, location..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-users" />
            </div>
            {activeFilters > 0 && (
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 whitespace-nowrap" onClick={() => { setRoleFilter('all'); setTeamFilter('all'); setDeptFilter('all'); setStatusFilter('all'); setSalaryFilter('all'); }}>
                Clear {activeFilters} filter{activeFilters > 1 ? 's' : ''}
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <Select value={deptFilter} onValueChange={setDeptFilter}>
              <SelectTrigger className="w-40" data-testid="filter-department"><SelectValue placeholder="Department" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                <SelectItem value="sales">Sales</SelectItem>
                <SelectItem value="recruiting">Recruiting</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-44" data-testid="filter-role"><SelectValue placeholder="Role" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {roles.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={teamFilter} onValueChange={setTeamFilter}>
              <SelectTrigger className="w-48" data-testid="filter-team"><SelectValue placeholder="Team" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                <SelectItem value="__unassigned__">Unassigned (No Team)</SelectItem>
                {teams.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
            {canSeeSalary && <Select value={salaryFilter} onValueChange={setSalaryFilter}>
              <SelectTrigger className="w-40" data-testid="filter-salary"><SelectValue placeholder="Salary" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Salary</SelectItem>
                <SelectItem value="set">Salary Set</SelectItem>
                <SelectItem value="unset">No Salary</SelectItem>
              </SelectContent>
            </Select>}
            <Select value={orgFilter} onValueChange={setOrgFilter}>
              <SelectTrigger className="w-48" data-testid="filter-org"><SelectValue placeholder="Organization" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Organizations</SelectItem>
                {allOrgs.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-slate-400">Showing {filtered.length} of {users.length} users</p>
        </CardContent>
      </Card>

      {/* View Toggle */}
      <div className="flex items-center gap-3">
        <Button
          variant={groupByTeam ? 'default' : 'outline'}
          size="sm"
          onClick={() => setGroupByTeam(!groupByTeam)}
          data-testid="toggle-group-by-team"
          className={groupByTeam ? 'bg-blue-600 hover:bg-blue-700' : ''}
        >
          <UsersThree size={16} className="mr-1.5" /> {groupByTeam ? 'Grouped by Team' : 'Group by Team'}
        </Button>
        {groupByTeam && <p className="text-xs text-slate-500">Users organized under their respective teams</p>}
      </div>

      {/* Employee Directory Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><SpinnerGap size={32} className="animate-spin text-blue-600 mx-auto" /></div>
          ) : groupByTeam ? (
            <GroupedByTeamView
              users={filtered}
              currentUser={currentUser}
              getRoleLabel={getRoleLabel}
              formatCurrency={formatCurrency}
              openEditModal={openEditModal}
              openPlacementDetails={openPlacementDetails}
              openSupportedDetails={openSupportedDetails}
              handleDeleteUser={isFullAdmin ? handleDeleteUser : null}
              setPasswordUser={setPasswordUser}
              setNewPassword={setNewPassword}
              setShowPasswordModal={setShowPasswordModal}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {['Emp ID', 'First Name', 'Last Name', 'Email', 'Role', 'Team', 'Organization', 'Location', 'Contact'].map(h => (
                      <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                    {/* Sticky Actions column — always visible at the right edge regardless of horizontal scroll */}
                    <th className="sticky right-0 z-20 bg-slate-50 px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap shadow-[-4px_0_4px_-2px_rgba(0,0,0,0.05)] border-l border-slate-200">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((user) => (
                    <tr key={user.id} className="group hover:bg-slate-50 transition-colors" data-testid={`user-row-${user.id}`}>
                      <td className="px-3 py-3 text-xs"><InlineEditCell userId={user.id} field="employee_id" value={user.employee_id} className="font-mono text-slate-600" /></td>
                      <td className="px-3 py-3">
                        {canSeeSalary ? (
                          <button type="button" onClick={() => navigate(`/dashboard/user/${user.id}`)} className="font-medium text-blue-700 hover:text-blue-900 underline cursor-pointer whitespace-nowrap text-xs" data-testid={`user-link-${user.id}`}>
                            <InlineEditCell userId={user.id} field="first_name" value={user.first_name || user.name?.split(' ')[0]} className="font-medium text-blue-700 whitespace-nowrap text-xs pointer-events-none" />
                          </button>
                        ) : (
                          <InlineEditCell userId={user.id} field="first_name" value={user.first_name || user.name?.split(' ')[0]} className="font-medium text-slate-900 whitespace-nowrap text-xs" />
                        )}
                        {!user.is_verified && <span className="text-[10px] text-amber-600 font-medium ml-1">PENDING</span>}
                      </td>
                      <td className="px-3 py-3 text-xs"><InlineEditCell userId={user.id} field="last_name" value={user.last_name || user.name?.split(' ').slice(1).join(' ')} className="text-slate-700" /></td>
                      <td className="px-3 py-3 text-slate-500 text-xs">{user.email}</td>
                      <td className="px-3 py-3"><Badge variant="outline" className="text-[10px] whitespace-nowrap">{getRoleLabel(user.role)}</Badge></td>
                      <td className="px-3 py-3 text-xs"><InlineEditCell userId={user.id} field="team" value={user.team} className="text-slate-600" /></td>
                      <td className="px-3 py-3 text-xs text-slate-600">{user.organization || '-'}</td>
                      <td className="px-3 py-3 text-xs text-slate-600 whitespace-nowrap">{user.work_location || user.location || '-'}</td>
                      <td className="px-3 py-3 text-xs text-slate-600 whitespace-nowrap">{user.direct_number || user.contact_number || '-'}</td>
                      <td className="sticky right-0 z-10 bg-white group-hover:bg-slate-50 px-3 py-3 shadow-[-4px_0_4px_-2px_rgba(0,0,0,0.05)] border-l border-slate-200">
                        {user.email !== currentUser?.email && (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="default"
                              size="sm"
                              className="h-7 px-3 gap-1 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                              onClick={() => openEditModal(user)}
                              data-testid={`edit-user-btn-${user.id}`}
                            >
                              <PencilSimple size={13} weight="bold" /> Edit
                            </Button>
                            {isFullAdmin && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 gap-1 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                              onClick={() => setMergePrimaryUser(user)}
                              data-testid={`merge-row-btn-${user.id}`}
                              title="Merge other users INTO this account (this user is kept as primary)"
                            >
                              <UsersThree size={13} /> Merge
                            </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-7 w-7"><DotsThree size={18} weight="bold" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => { setPasswordUser(user); setNewPassword(''); setShowPasswordModal(true); }}>
                                  <Key size={16} className="mr-2" /> Reset Password
                                </DropdownMenuItem>
                                {showInactive ? (
                                  <DropdownMenuItem
                                    onClick={() => handleRestoreUser(user)}
                                    data-testid={`restore-user-${user.id}`}
                                    className="text-emerald-700"
                                  >
                                    <ArrowCounterClockwise size={16} className="mr-2" /> Restore Employee
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem
                                    onClick={() => handleMoveToLeft(user)}
                                    data-testid={`move-to-left-${user.id}`}
                                    className="text-amber-700"
                                  >
                                    <SignOut size={16} className="mr-2" /> Move to Left Employees
                                  </DropdownMenuItem>
                                )}
                                {isFullAdmin && (
                                <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteUser(user.id)}>
                                  <Trash size={16} className="mr-2" /> Delete (Permanent)
                                </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="p-8 text-center text-slate-500">No users match your filters</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle>Edit User - {editUser?.name}</DialogTitle>
            <DialogDescription>Update employee details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-6 space-y-4 pb-4">
            {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">{error}</div>}
            <div className="grid grid-cols-2 gap-4">
              {/* Read-only email display */}
              <div className="space-y-2 col-span-2">
                <Label>Email</Label>
                <Input value={editUser?.email || ''} disabled className="bg-slate-50 text-slate-500" />
              </div>
              <div className="space-y-2">
                <Label>Employee ID</Label>
                <Input value={editData.employee_id} onChange={(e) => setEditData({...editData, employee_id: e.target.value})} placeholder="EMP001" data-testid="edit-employee-id" />
              </div>
              <div className="space-y-2">
                <Label>Designation</Label>
                <Input value={editData.designation} onChange={(e) => setEditData({...editData, designation: e.target.value})} onBlur={(e) => setEditData(d => ({...d, designation: toTitleCase(e.target.value)}))} placeholder="Sr. Recruiter" data-testid="edit-designation" />
              </div>
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input value={editData.first_name} onChange={(e) => setEditData({...editData, first_name: e.target.value})} onBlur={(e) => setEditData(d => ({...d, first_name: toTitleCase(e.target.value)}))} data-testid="edit-first-name" />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input value={editData.last_name} onChange={(e) => setEditData({...editData, last_name: e.target.value})} onBlur={(e) => setEditData(d => ({...d, last_name: toTitleCase(e.target.value)}))} data-testid="edit-last-name" />
              </div>
              <div className="space-y-2">
                <Label>Direct Number</Label>
                <Input value={editData.direct_number} onChange={(e) => setEditData({...editData, direct_number: e.target.value})} placeholder="+1 234-567-8900" data-testid="edit-direct-number" />
              </div>
              {canSeeSalary && <div className="space-y-2">
                <Label>Salary (Monthly ₹)</Label>
                <Input type="number" value={editData.salary} onChange={(e) => setEditData({...editData, salary: e.target.value})} placeholder="50000" data-testid="edit-salary" />
              </div>}
              <div className="space-y-2">
                <Label>Team</Label>
                <Select value={editData.team || '_none'} onValueChange={(v) => setEditData({...editData, team: v === '_none' ? '' : v})}>
                  <SelectTrigger data-testid="edit-team"><SelectValue placeholder="Select team" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">-- No Team --</SelectItem>
                    {[...new Set(users.map(u => u.team).filter(Boolean))].sort().map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={editData.role} onValueChange={(v) => setEditData({...editData, role: v})}>
                  <SelectTrigger data-testid="edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{assignableRoles.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Work Location</Label>
                <Select value={editData.work_location || '_none'} onValueChange={(v) => setEditData({...editData, work_location: v === '_none' ? '' : v})}>
                  <SelectTrigger data-testid="edit-work-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">-- No Location --</SelectItem>
                    {workLocations.map(loc => <SelectItem key={loc.name} value={loc.name}>{loc.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Organization</Label>
                <Select value={editData.organization || '_none'} onValueChange={(v) => setEditData({...editData, organization: v === '_none' ? '' : v})}>
                  <SelectTrigger data-testid="edit-organization"><SelectValue placeholder="Select organization" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">-- No Organization --</SelectItem>
                    {organizations.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reporting Manager (single)</Label>
                <Select value={editData.manager_id || '_none'} onValueChange={(v) => setEditData({...editData, manager_id: v === '_none' ? '' : v})}>
                  <SelectTrigger data-testid="edit-manager-id"><SelectValue placeholder="-- No manager --" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="_none">-- No manager --</SelectItem>
                    {reportingManagerOptions.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name} ({getRoleLabel(u.role)})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reporting Lead (single)</Label>
                <Select value={editData.reporting_lead_id || '_none'} onValueChange={(v) => setEditData({...editData, reporting_lead_id: v === '_none' ? '' : v})}>
                  <SelectTrigger data-testid="edit-reporting-lead-id"><SelectValue placeholder="-- No lead --" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="_none">-- No lead --</SelectItem>
                    {activeUserOptions.filter((u) => ['sales_lead', 'recruiting_lead', 'sales_manager', 'recruiting_manager'].includes(u.role) || u.id === editData.reporting_lead_id).map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name} ({getRoleLabel(u.role)})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editData.is_active !== false}
                    onChange={(e) => setEditData({...editData, is_active: e.target.checked})}
                    data-testid="edit-is-active"
                  />
                  <span className="font-medium">Active employee</span>
                  <span className="text-xs text-slate-500">(uncheck if the employee has left)</span>
                </label>
              </div>
              {/* Reporting Managers Multi-Select */}
              <div className="space-y-2 col-span-2">
                <Label>Reporting Manager(s)</Label>
                <div className="border rounded-md p-2 max-h-32 overflow-y-auto bg-white" data-testid="edit-reporting-managers">
                  {/* Selected badges */}
                  {(editData.reporting_managers || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {editData.reporting_managers.map(rmId => {
                        const mgr = users.find(u => u.id === rmId);
                        return mgr ? (
                          <Badge key={rmId} variant="secondary" className="text-[10px] gap-1 pr-1">
                            {mgr.name} <span className="text-[8px] text-slate-400">({getRoleLabel(mgr.role)})</span>
                            <button type="button" className="ml-1 text-slate-500 hover:text-red-600" onClick={(e) => { e.stopPropagation(); e.preventDefault(); setEditData(prev => ({...prev, reporting_managers: (prev.reporting_managers || []).filter(id => id !== rmId)})); }}>×</button>
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                  {/* All active users — pick any combo of leads/managers/peers/seniors */}
                  <div className="space-y-0.5">
                    {activeUserOptions.map(mgr => (
                      <label key={mgr.id} className="flex items-center gap-2 p-1 rounded hover:bg-slate-50 cursor-pointer text-xs">
                        <input type="checkbox" checked={(editData.reporting_managers || []).includes(mgr.id)}
                          onChange={(e) => {
                            const current = editData.reporting_managers || [];
                            setEditData({...editData, reporting_managers: e.target.checked ? [...current, mgr.id] : current.filter(id => id !== mgr.id)});
                          }}
                          className="rounded border-slate-300"
                        />
                        <span className="text-slate-800">{mgr.name}</span>
                        <span className="text-[9px] text-slate-400">({getRoleLabel(mgr.role)})</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              {/* Onsite Manager(s) Multi-Select */}
              <div className="space-y-2 col-span-2">
                <Label>Onsite Manager(s)</Label>
                <div className="border rounded-md p-2 max-h-28 overflow-y-auto bg-white" data-testid="edit-onsite-managers">
                  {(editData.onsite_managers || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {editData.onsite_managers.map(omId => {
                        const mgr = users.find(u => u.id === omId);
                        return mgr ? (
                          <Badge key={omId} variant="secondary" className="text-[10px] gap-1 pr-1 bg-orange-50 text-orange-700">
                            {mgr.name}
                            <button type="button" className="ml-1 text-slate-500 hover:text-red-600" onClick={(e) => { e.stopPropagation(); e.preventDefault(); setEditData(prev => ({...prev, onsite_managers: (prev.onsite_managers || []).filter(id => id !== omId)})); }}>×</button>
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                  <div className="space-y-0.5">
                    {users.filter(u => u.role === 'onsite_manager' && u.id !== editUser?.id).map(om => (
                      <label key={om.id} className="flex items-center gap-2 p-1 rounded hover:bg-slate-50 cursor-pointer text-xs">
                        <input type="checkbox" checked={(editData.onsite_managers || []).includes(om.id)}
                          onChange={(e) => {
                            const current = editData.onsite_managers || [];
                            setEditData({...editData, onsite_managers: e.target.checked ? [...current, om.id] : current.filter(id => id !== om.id)});
                          }}
                          className="rounded border-slate-300"
                        />
                        <span className="text-slate-800">{om.name}</span>
                        <span className="text-[9px] text-orange-500">Onsite Manager</span>
                      </label>
                    ))}
                    {users.filter(u => u.role === 'onsite_manager' && u.id !== editUser?.id).length === 0 && (
                      <p className="text-xs text-slate-400 py-1">No onsite managers available</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            </div>
            <DialogFooter className="px-6 py-4 border-t shrink-0 bg-background">
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={editLoading}>
                {editLoading && <SpinnerGap size={16} className="animate-spin mr-2" />} Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Work Locations Modal */}
      <Dialog open={showLocationsModal} onOpenChange={setShowLocationsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Work Locations</DialogTitle>
            <DialogDescription>Add or remove locations that can be assigned to employees</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                placeholder="New location name..."
                value={newLocation}
                onChange={(e) => setNewLocation(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addWorkLocation()}
                className="flex-1"
                data-testid="new-location-input"
              />
              <Button onClick={addWorkLocation} className="bg-blue-600 hover:bg-blue-700" size="sm" data-testid="add-location-btn">
                <Plus size={16} className="mr-1" /> Add
              </Button>
            </div>
            <div className="border rounded-md divide-y max-h-60 overflow-y-auto">
              {workLocations.length === 0 ? (
                <p className="text-sm text-slate-400 p-3 text-center">No locations added yet</p>
              ) : workLocations.map(loc => (
                <div key={loc.name} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50" data-testid={`location-${loc.name}`}>
                  <span className="text-sm text-slate-700">{loc.name}</span>
                  <button onClick={() => deleteWorkLocation(loc.name)} className="text-slate-400 hover:text-red-600 transition-colors" data-testid={`delete-location-${loc.name}`}>
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Placement/Backout Details Modal */}
      <Dialog open={showPlacementModal} onOpenChange={setShowPlacementModal}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detailType === 'backouts' ? 'Backout' : detailType === 'supported' ? 'Supported Placement' : 'Placement'} Details - {detailUser?.name}</DialogTitle>
            <DialogDescription>{placementDetails.length} {detailType === 'supported' ? 'supported placement' : detailType === 'backouts' ? 'backout' : 'placement'}(s) found</DialogDescription>
          </DialogHeader>
          {placementLoading ? (
            <div className="p-8 text-center"><SpinnerGap size={32} className="animate-spin text-blue-600 mx-auto" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    {['ID', 'Candidate', 'Category', 'Module', 'Status', 'Bill Rate', 'Pay Rate', 'Margin', 'Total Incentive', 'SR', 'SRC', 'SL', 'SM', 'SRCL', 'SRCM', 'Start Date'].map(h => (
                      <th key={h} className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {placementDetails.map((p, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-2 py-2">
                        <button onClick={() => { setShowPlacementModal(false); navigate(`/dashboard/placements/${p.id}`); }}
                          className="text-blue-600 hover:text-blue-800 underline font-mono text-[10px] whitespace-nowrap"
                          data-testid={`placement-link-${p.id}`}>
                          {p.id?.slice(-6) || '-'}
                        </button>
                      </td>
                      <td className="px-2 py-2 font-medium">{p.candidate_name}</td>
                      <td className="px-2 py-2">{p.category?.replace(/_/g, ' ')}</td>
                      <td className="px-2 py-2"><Badge variant="outline" className="text-[10px]">{p.module}</Badge></td>
                      <td className="px-2 py-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          p.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                          p.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          p.status === 'backed_out' ? 'bg-orange-100 text-orange-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>{p.status?.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="px-2 py-2">${p.bill_rate}</td>
                      <td className="px-2 py-2">${p.pay_rate}</td>
                      <td className="px-2 py-2 font-medium">${(p.margin || 0).toFixed(2)}</td>
                      <td className="px-2 py-2 font-bold text-emerald-700">{formatCurrency(p.total_placement)}</td>
                      <td className="px-2 py-2">{formatCurrency(p.module === 'recruiting' ? p.recruiter : p.sales_recruiter)}</td>
                      <td className="px-2 py-2">{formatCurrency(p.sourcing_recruiter)}</td>
                      <td className="px-2 py-2">{formatCurrency(p.module === 'recruiting' ? p.lead : p.sales_lead)}</td>
                      <td className="px-2 py-2">{formatCurrency(p.module === 'recruiting' ? p.manager : p.sales_manager)}</td>
                      <td className="px-2 py-2">{formatCurrency(p.sourcing_lead)}</td>
                      <td className="px-2 py-2">{formatCurrency(p.sourcing_manager)}</td>
                      <td className="px-2 py-2 whitespace-nowrap">{p.start_date || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {placementDetails.length > 0 && (
                <div className="p-3 bg-slate-50 border-t border-slate-200 flex gap-6">
                  <span className="text-sm font-medium text-slate-700">
                    Total Incentive: <strong className="text-emerald-700">{formatCurrency(placementDetails.reduce((s, p) => s + (p.total_placement || 0), 0))}</strong>
                  </span>
                  <span className="text-sm text-slate-500">
                    Avg Margin: ${(placementDetails.reduce((s, p) => s + (p.margin || 0), 0) / placementDetails.length).toFixed(2)}/hr
                  </span>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create User Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>An OTP will be sent to the user's email for account verification.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-6 space-y-4 pb-4">
            {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">{error}</div>}
            {success && <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-md text-sm">{success}</div>}
            <div className="space-y-2"><Label>Name *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Doe" required data-testid="input-user-name" /></div>
            <div className="space-y-2"><Label>Email *</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@intellectt.com" required data-testid="input-user-email" /></div>
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})} required>
                <SelectTrigger data-testid="select-user-role"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>{assignableRoles.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Team (Optional)</Label>
              <Select value={formData.team || '_none'} onValueChange={(v) => setFormData({...formData, team: v === '_none' ? '' : v})}>
                <SelectTrigger data-testid="input-user-team"><SelectValue placeholder="Select team" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">-- No Team --</SelectItem>
                  {[...new Set(users.map(u => u.team).filter(Boolean))].sort().map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Organization</Label>
              <Select value={formData.organization || '_none'} onValueChange={(v) => setFormData({...formData, organization: v === '_none' ? '' : v})}>
                <SelectTrigger data-testid="input-user-organization"><SelectValue placeholder="Select organization" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">-- No Organization --</SelectItem>
                  {organizations.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Work Location</Label>
              <Select value={formData.work_location || '_none'} onValueChange={(v) => setFormData({...formData, work_location: v === '_none' ? '' : v})}>
                <SelectTrigger data-testid="input-user-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">-- No Location --</SelectItem>
                  {workLocations.map(loc => <SelectItem key={loc.name} value={loc.name}>{loc.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reporting Manager(s)</Label>
              <div className="border rounded-md p-2 max-h-28 overflow-y-auto bg-white" data-testid="input-user-reporting-managers">
                {users.filter(u => ['sales_manager', 'recruiting_manager', 'onsite_manager', 'sales_assistant_manager', 'recruiting_assistant_manager', 'sales_lead', 'recruiting_lead', 'ceo'].includes(u.role)).map(mgr => (
                  <label key={mgr.id} className="flex items-center gap-2 p-1 rounded hover:bg-slate-50 cursor-pointer text-xs">
                    <input type="checkbox" checked={(formData.reporting_managers || []).includes(mgr.id)}
                      onChange={(e) => {
                        const current = formData.reporting_managers || [];
                        setFormData({...formData, reporting_managers: e.target.checked ? [...current, mgr.id] : current.filter(id => id !== mgr.id)});
                      }} className="rounded border-slate-300" />
                    <span className="text-slate-800">{mgr.name}</span>
                    <span className="text-[9px] text-slate-400">({getRoleLabel(mgr.role)})</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Onsite Manager(s)</Label>
              <div className="border rounded-md p-2 max-h-28 overflow-y-auto bg-white" data-testid="input-user-onsite-managers">
                {users.filter(u => u.role === 'onsite_manager').map(om => (
                  <label key={om.id} className="flex items-center gap-2 p-1 rounded hover:bg-slate-50 cursor-pointer text-xs">
                    <input type="checkbox" checked={(formData.onsite_managers || []).includes(om.id)}
                      onChange={(e) => {
                        const current = formData.onsite_managers || [];
                        setFormData({...formData, onsite_managers: e.target.checked ? [...current, om.id] : current.filter(id => id !== om.id)});
                      }} className="rounded border-slate-300" />
                    <span className="text-slate-800">{om.name}</span>
                    <span className="text-[9px] text-orange-500">Onsite Manager</span>
                  </label>
                ))}
                {users.filter(u => u.role === 'onsite_manager').length === 0 && (
                  <p className="text-xs text-slate-400 py-1">No onsite managers available</p>
                )}
              </div>
            </div>
            </div>
            <DialogFooter className="px-6 py-4 border-t shrink-0 bg-background">
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={createLoading}>
                {createLoading && <SpinnerGap size={16} className="animate-spin mr-2" />} Create & Send OTP
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Reset Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>Set a new password for {passwordUser?.name || passwordUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New Password *</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 6 characters" data-testid="reset-password-input" />
              <p className="text-xs text-slate-400">Must be at least 6 characters. User will use this to log in.</p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowPasswordModal(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleResetPassword} data-testid="reset-password-confirm">
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Bulk Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={(open) => { setShowUploadModal(open); if (!open) resetUpload(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle>Bulk Upload Users</DialogTitle>
            <DialogDescription>Upload a CSV file to create multiple users at once</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 pb-4">
          <div className="space-y-6">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2"><FileText size={18} />Step 1: Download Excel Template</h4>
              <p className="text-sm text-blue-600 mb-3">Template includes headers: First Name, Last Name, Email, Password, Role, Team, Designation, Employee ID, Salary, Phone, Lead Email, Manager Email. An "Instructions" sheet is included.</p>
              <Button variant="outline" onClick={handleDownloadTemplate} className="border-blue-300 text-blue-700 hover:bg-blue-100">
                <DownloadSimple size={16} className="mr-2" /> Download Template (.xlsx)
              </Button>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="font-medium text-slate-800 mb-2 flex items-center gap-2"><FileArrowUp size={18} />Step 2: Upload Your File</h4>
              <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${uploadOptions.selectedFile ? 'border-emerald-300 bg-emerald-50' : 'border-slate-300 hover:border-blue-400'}`}>
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx" onChange={handleFileSelect} className="hidden" id="csv-upload" />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  {uploadOptions.selectedFile ? (
                    <div className="flex items-center justify-center gap-3"><CheckCircle size={24} className="text-emerald-600" /><p className="font-medium text-emerald-700">{uploadOptions.selectedFile.name}</p></div>
                  ) : (
                    <div><UploadSimple size={32} className="mx-auto mb-2 text-slate-400" /><p className="text-sm text-slate-600">Click to select XLSX or CSV file</p></div>
                  )}
                </label>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Checkbox id="send-emails" checked={uploadOptions.sendEmails} onCheckedChange={(checked) => setUploadOptions({...uploadOptions, sendEmails: checked})} />
                <Label htmlFor="send-emails" className="text-sm cursor-pointer">Send OTP verification emails</Label>
              </div>
            </div>
            {uploadResult && (
              <div>{uploadResult.error ? (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg"><XCircle size={20} className="text-red-600 inline mr-2" /><span className="text-red-700">{uploadResult.error}</span></div>
              ) : (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg"><CheckCircle size={20} className="text-emerald-600 inline mr-2" /><span className="text-emerald-700">{uploadResult.message}</span></div>
              )}</div>
            )}
          </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t shrink-0 bg-background">
            <Button type="button" variant="outline" onClick={() => { setShowUploadModal(false); resetUpload(); }}>{uploadResult ? 'Close' : 'Cancel'}</Button>
            {!uploadResult && <Button onClick={handleUpload} className="bg-blue-600 hover:bg-blue-700" disabled={uploadLoading || !uploadOptions.selectedFile}>
              {uploadLoading && <SpinnerGap size={16} className="animate-spin mr-2" />} Upload & Create
            </Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Preview Modal */}
      <ExportPreviewModal open={showExport} onClose={() => setShowExport(false)}
        data={filtered} fileName={`Employee_Directory_${new Date().toISOString().slice(0,10)}`}
        title="Employee Directory Export Preview"
        columns={[
          { key: 'emp_id', header: 'Employee ID', accessor: r => r.employee_id || '' },
          { key: 'name', header: 'Full Name', accessor: r => r.name },
          { key: 'email', header: 'Email', accessor: r => r.email },
          { key: 'role', header: 'Role', accessor: r => getRoleLabel(r.role) },
          { key: 'org', header: 'Organization', accessor: r => r.organization || '' },
          { key: 'team', header: 'Team', accessor: r => r.team || '' },
          { key: 'salary', header: 'Salary (INR)', accessor: r => r.salary || 0 },
          { key: 'location', header: 'Location', accessor: r => r.location || '' },
          { key: 'verified', header: 'Verified', accessor: r => r.is_verified ? 'Yes' : 'No' },
        ]}
      />

      {/* Merge Duplicate Users (page-header mode: auto-detect + manual) */}
      <MergeUsersDialog
        open={showMergeModal}
        onOpenChange={setShowMergeModal}
        allUsers={users}
        onMerged={() => fetchUsers(showInactive)}
      />
      {/* Row-triggered Merge — primary user is the row clicked */}
      <MergeUsersDialog
        open={!!mergePrimaryUser}
        onOpenChange={(v) => { if (!v) setMergePrimaryUser(null); }}
        allUsers={users}
        primaryUser={mergePrimaryUser}
        onMerged={() => fetchUsers(showInactive)}
      />
    </div>
  );
};

// Grouped by Team view component
const GroupedByTeamView = ({ users, currentUser, getRoleLabel, formatCurrency, openEditModal, openPlacementDetails, openSupportedDetails, handleDeleteUser, setPasswordUser, setNewPassword, setShowPasswordModal }) => {
  const teamGroups = {};
  const noTeam = [];
  users.forEach(u => {
    if (u.team) {
      if (!teamGroups[u.team]) teamGroups[u.team] = [];
      teamGroups[u.team].push(u);
    } else {
      noTeam.push(u);
    }
  });

  const renderUserRow = (user) => (
    <tr key={user.id} className="hover:bg-slate-50 transition-colors" data-testid={`user-row-${user.id}`}>
      <td className="px-3 py-2.5 text-slate-600 font-mono text-xs">{user.employee_id || '-'}</td>
      <td className="px-3 py-2.5">
        <p className="font-medium text-slate-900 whitespace-nowrap text-xs">{user.first_name || user.name?.split(' ')[0] || '-'}</p>
        {!user.is_verified && <span className="text-[10px] text-amber-600 font-medium">PENDING</span>}
      </td>
      <td className="px-3 py-2.5 text-slate-700 text-xs">{user.last_name || user.name?.split(' ').slice(1).join(' ') || '-'}</td>
      <td className="px-3 py-2.5 text-slate-500 text-xs">{user.email}</td>
      <td className="px-3 py-2.5"><Badge variant="outline" className="text-[10px] whitespace-nowrap">{getRoleLabel(user.role)}</Badge></td>
      {canSeeSalary && <td className="px-3 py-2.5 text-slate-700 font-medium text-xs whitespace-nowrap">{user.salary ? formatCurrency(user.salary) : '-'}</td>}
      <td className="px-3 py-2.5 text-center">
        <button onClick={() => user.placement_count > 0 && openPlacementDetails(user, 'placements')}
          className={`font-bold text-xs ${user.placement_count > 0 ? 'text-blue-600 hover:text-blue-800 underline cursor-pointer' : 'text-slate-400 cursor-default'}`}>
          {user.placement_count || 0}
        </button>
      </td>
      <td className="px-3 py-2.5 text-center">
        <button onClick={() => user.supported_count > 0 && openSupportedDetails(user)}
          className={`font-bold text-xs ${user.supported_count > 0 ? 'text-purple-600 hover:text-purple-800 underline cursor-pointer' : 'text-slate-400 cursor-default'}`}>
          {user.supported_count || 0}
        </button>
      </td>
      <td className="px-3 py-2.5 text-center">
        <button onClick={() => user.backout_count > 0 && openPlacementDetails(user, 'backouts')}
          className={`font-bold text-xs ${user.backout_count > 0 ? 'text-red-600 hover:text-red-800 underline cursor-pointer' : 'text-slate-400 cursor-default'}`}>
          {user.backout_count || 0}
        </button>
      </td>
      <td className="px-3 py-2.5">
        {user.email !== currentUser?.email && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7"><DotsThree size={16} weight="bold" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => openEditModal(user)}>
                <PencilSimple size={14} className="mr-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setPasswordUser(user); setNewPassword(''); setShowPasswordModal(true); }}>
                <Key size={14} className="mr-2" /> Reset Password
              </DropdownMenuItem>
              {handleDeleteUser && (
              <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteUser(user.id)}>
                <Trash size={14} className="mr-2" /> Delete
              </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </td>
    </tr>
  );

  const headers = ['Emp ID', 'First Name', 'Last Name', 'Email', 'Role', ...(canSeeSalary ? ['Salary'] : []), 'Placements', 'Supported', 'Backouts', 'Actions'];

  return (
    <div>
      {Object.entries(teamGroups).sort(([a], [b]) => a.localeCompare(b)).map(([teamName, teamUsers]) => (
        <div key={teamName} data-testid={`team-group-${teamName}`}>
          <div className="bg-slate-100 px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UsersThree size={16} className="text-slate-600" />
              <span className="font-semibold text-sm text-slate-800">{teamName}</span>
              <Badge variant="outline" className="text-[10px]">{teamUsers.length} members</Badge>
            </div>
            <div className="flex gap-4 text-xs text-slate-500">
              <span>Lead: <strong className="text-slate-700">{teamUsers[0]?.lead_name || '-'}</strong></span>
              <span>Manager: <strong className="text-slate-700">{teamUsers[0]?.manager_name || '-'}</strong></span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/50 border-b border-slate-100">
                <tr>
                  {headers.map(h => (
                    <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {teamUsers.map(renderUserRow)}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {noTeam.length > 0 && (
        <div data-testid="team-group-unassigned">
          <div className="bg-amber-50 px-4 py-2.5 border-b border-amber-200 flex items-center gap-2">
            <UsersThree size={16} className="text-amber-600" />
            <span className="font-semibold text-sm text-amber-800">Unassigned (No Team)</span>
            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600">{noTeam.length} users</Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/50 border-b border-slate-100">
                <tr>
                  {headers.map(h => (
                    <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {noTeam.map(renderUserRow)}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {users.length === 0 && (
        <div className="p-8 text-center text-slate-500">No users match your filters</div>
      )}
    </div>
  );
};

export default UserManagementPage;
